"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  addCartItem,
  clearCart,
  fetchCart,
  removeCartItem,
  updateCartItem,
} from "@/lib/api";
import type { Cart, CartItemRequest } from "@/types/cart";

interface CartContextValue {
  cart: Cart | null;
  isLoading: boolean;
  error: string | null;
  addItem: (request: CartItemRequest) => Promise<void>;
  updateQuantity: (id: string, quantity: number) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  clear: () => Promise<void>;
  refresh: () => Promise<void>;
}

const CartContext = createContext<CartContextValue | null>(null);

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchCart();
      setCart(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load cart");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addItem = useCallback(async (request: CartItemRequest) => {
    const data = await addCartItem(request);
    setCart(data);
  }, []);

  const updateQuantity = useCallback(async (id: string, quantity: number) => {
    const data = await updateCartItem(id, quantity);
    setCart(data);
  }, []);

  const removeItem = useCallback(async (id: string) => {
    const data = await removeCartItem(id);
    setCart(data);
  }, []);

  const clear = useCallback(async () => {
    const data = await clearCart();
    setCart(data);
  }, []);

  const value = useMemo(
    () => ({
      cart,
      isLoading,
      error,
      addItem,
      updateQuantity,
      removeItem,
      clear,
      refresh,
    }),
    [cart, isLoading, error, addItem, updateQuantity, removeItem, clear, refresh],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
